import { ReactNode, createContext, useContext, useState } from "react";

// ============================================
// Legacy Tabs API (for backward compatibility)
// ============================================

export interface Tab {
  id: string;
  label: string;
  content: ReactNode;
}

interface LegacyTabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
}

export function LegacyTabs({ tabs, activeTab, onChange }: LegacyTabsProps) {
  return (
    <div className="w-full">
      <div className="border-b-2 border-border">
        <nav className="-mb-0.5 flex space-x-2 overflow-x-auto scrollbar-hide" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={`
                whitespace-nowrap py-2.5 px-3 md:py-3 md:px-6 border-b-4 font-bold text-sm transition-all duration-200 rounded-t-xl
                ${
                  activeTab === tab.id
                    ? "border-primary text-primary bg-primary/10"
                    : "border-transparent text-muted-foreground hover:text-primary hover:bg-primary/5"
                }
              `}
              aria-current={activeTab === tab.id ? "page" : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="mt-6">
        {tabs.find((tab) => tab.id === activeTab)?.content}
      </div>
    </div>
  );
}

// ============================================
// New Tabs API (Radix/shadcn style)
// ============================================

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs components must be used within a Tabs provider");
  }
  return context;
}

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children?: ReactNode;
  className?: string;
  // Legacy props support
  tabs?: Tab[];
  activeTab?: string;
  onChange?: (tabId: string) => void;
}

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  children,
  className = "",
  // Legacy props
  tabs,
  activeTab,
  onChange,
}: TabsProps) {
  // Always call useState to respect React Hook rules
  const [internalValue, setInternalValue] = useState(defaultValue || "");

  // If legacy props are provided, use legacy component
  if (tabs && activeTab !== undefined && onChange) {
    return <LegacyTabs tabs={tabs} activeTab={activeTab} onChange={onChange} />;
  }

  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = onValueChange || setInternalValue;

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={`w-full ${className}`}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className = "" }: TabsListProps) {
  return (
    <div className={`border-b-2 border-border ${className}`}>
      <nav className="-mb-0.5 flex space-x-2" aria-label="Tabs">
        {children}
      </nav>
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsTrigger({ value, children, className = "" }: TabsTriggerProps) {
  const { value: currentValue, onValueChange } = useTabsContext();
  const isActive = currentValue === value;

  return (
    <button
      onClick={() => onValueChange(value)}
      className={`
        whitespace-nowrap py-3 px-6 border-b-4 font-bold text-sm transition-all duration-200 rounded-t-xl
        ${
          isActive
            ? "border-primary text-primary bg-primary/10"
            : "border-transparent text-muted-foreground hover:text-primary hover:bg-primary/5"
        }
        ${className}
      `}
      aria-current={isActive ? "page" : undefined}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className = "" }: TabsContentProps) {
  const { value: currentValue } = useTabsContext();

  if (currentValue !== value) {
    return null;
  }

  return <div className={className}>{children}</div>;
}
